import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Copy, Eye, EyeOff, ArrowUp, ArrowDown, Upload, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { blogImage } from "@/lib/blog";
import { toast } from "sonner";

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const emptyPost = {
  title_it: "", title_en: "", slug: "", excerpt_it: "", excerpt_en: "", content_it: "", content_en: "",
  image_url: "", category_slug: "", author: "", status: "draft", featured: false, published_at: "",
  seo_title_it: "", seo_title_en: "", seo_desc_it: "", seo_desc_en: "",
  cta_type: "none", cta_product_id: "", cta_title_it: "", cta_title_en: "", cta_text_it: "", cta_text_en: "",
  cta_button_it: "", cta_button_en: "", cta_url: "", cta_new_tab: true, cta_disclosure_it: "", cta_disclosure_en: "",
};

const Field = ({ label, children }) => (<div><Label className="text-xs text-brand-navy/60">{label}</Label><div className="mt-1">{children}</div></div>);

const isEmptyHtml = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<br\s*\/?>/gi, "").replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim();
  return stripped.length === 0;
};

const PostForm = ({ open, onClose, onSaved, editingId, categories, products }) => {
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const originalContent = useRef({ it: "", en: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setSlugTouched(false);
    originalContent.current = { it: "", en: "" };
    if (editingId) {
      api.get(`/admin/blog/posts/${editingId}`).then(({ data }) => {
        originalContent.current = { it: data.content_it || "", en: data.content_en || "" };
        setForm({ ...emptyPost, ...data, published_at: data.published_at ? data.published_at.slice(0, 10) : "" });
      }).catch(() => {});
    } else { setForm(emptyPost); }
  }, [open, editingId]);

  const onTitleIt = (v) => { set("title_it", v); if (!slugTouched && !editingId) set("slug", slugify(v)); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/media/upload", fd); set("image_url", data.url); toast.success("Immagine caricata"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload non riuscito"); }
  };

  const save = async () => {
    if (!form.title_it || !form.title_en) { toast.error("Titolo IT ed EN obbligatori"); return; }
    if (!form.category_slug) { toast.error("Seleziona una categoria"); return; }
    // Protezione anti-svuotamento accidentale: se un contenuto era presente e ora risulta vuoto,
    // chiedi conferma esplicita (l'utente può comunque cancellare volontariamente).
    if (editingId) {
      const lostIt = !isEmptyHtml(originalContent.current.it) && isEmptyHtml(form.content_it);
      const lostEn = !isEmptyHtml(originalContent.current.en) && isEmptyHtml(form.content_en);
      if (lostIt || lostEn) {
        const which = [lostIt ? "IT" : null, lostEn ? "EN" : null].filter(Boolean).join(" e ");
        if (!window.confirm(`Attenzione: il Contenuto ${which} risulta VUOTO ma prima conteneva del testo. Salvare comunque svuotando il contenuto?`)) {
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = { ...form, published_at: form.published_at ? new Date(form.published_at).toISOString() : null };
      if (editingId) await api.put(`/admin/blog/posts/${editingId}`, payload);
      else await api.post("/admin/blog/posts", payload);
      toast.success("Articolo salvato"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const amazonProducts = products.filter((p) => p.product_type === "amazon");
  const digitalProducts = products.filter((p) => p.product_type !== "amazon");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blog-post-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editingId ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Compila i campi dell'articolo e salva.</DialogDescription>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Titolo IT *"><Input data-testid="blog-title-it" value={form.title_it} onChange={(e) => onTitleIt(e.target.value)} /></Field>
            <Field label="Titolo EN *"><Input data-testid="blog-title-en" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} /></Field>
          </div>
          <Field label="Slug (URL) — modificabile"><Input data-testid="blog-slug" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} placeholder="titolo-articolo" /></Field>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Categoria *">
              <select data-testid="blog-category" value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} className="w-full h-10 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
                <option value="">—</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name_it}</option>)}
              </select>
            </Field>
            <Field label="Autore"><Input data-testid="blog-author" value={form.author} onChange={(e) => set("author", e.target.value)} /></Field>
            <Field label="Data pubblicazione"><Input data-testid="blog-date" type="date" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} /></Field>
          </div>

          <div className="flex flex-wrap items-center gap-5 rounded-xl bg-brand-light p-3">
            <Field label="Stato">
              <select data-testid="blog-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="h-9 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
                <option value="draft">Bozza</option><option value="published">Pubblicato</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 mt-4"><Switch data-testid="blog-featured" checked={form.featured} onCheckedChange={(v) => set("featured", v)} /> <span className="text-sm">In evidenza</span></label>
          </div>

          <div className="rounded-xl bg-brand-light p-3 flex items-center gap-4">
            <div className="h-20 w-32 rounded-lg bg-white border overflow-hidden flex items-center justify-center shrink-0">
              {form.image_url ? <img src={blogImage(form.image_url)} alt="" className="w-full h-full object-cover" /> : <FileText className="h-6 w-6 text-brand-navy/30" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-brand-navy">Immagine principale</p>
              <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-semibold text-brand-electric mt-1">
                <Upload className="h-4 w-4" /> Carica <input data-testid="blog-image-upload" type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              </label>
              <Input data-testid="blog-image-url" value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="oppure incolla URL (Media Manager)" className="mt-2 h-8 text-xs" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Estratto IT"><Textarea data-testid="blog-excerpt-it" rows={2} value={form.excerpt_it} onChange={(e) => set("excerpt_it", e.target.value)} /></Field>
            <Field label="Estratto EN"><Textarea data-testid="blog-excerpt-en" rows={2} value={form.excerpt_en} onChange={(e) => set("excerpt_en", e.target.value)} /></Field>
          </div>

          <Field label="Contenuto IT"><RichTextEditor testid="blog-content-it" value={form.content_it} onChange={(v) => set("content_it", v)} /></Field>
          <Field label="Contenuto EN"><RichTextEditor testid="blog-content-en" value={form.content_en} onChange={(v) => set("content_en", v)} /></Field>

          {/* CTA */}
          <div className="rounded-xl border border-brand-navy/10 p-4 space-y-3">
            <p className="text-sm font-semibold text-brand-navy">Call to action (facoltativa)</p>
            <select data-testid="blog-cta-type" value={form.cta_type} onChange={(e) => set("cta_type", e.target.value)} className="w-full h-10 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
              <option value="none">Nessuna CTA</option>
              <option value="product">Prodotto digitale LearnSphereHub</option>
              <option value="amazon">Libro / prodotto Amazon</option>
              <option value="external">Link esterno</option>
            </select>
            {form.cta_type === "product" && (
              <select data-testid="blog-cta-product" value={form.cta_product_id || ""} onChange={(e) => set("cta_product_id", e.target.value)} className="w-full h-10 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
                <option value="">— seleziona prodotto —</option>
                {digitalProducts.map((p) => <option key={p.id} value={p.id}>{p.title_it}</option>)}
              </select>
            )}
            {form.cta_type === "amazon" && (
              <select data-testid="blog-cta-amazon" value={form.cta_product_id || ""} onChange={(e) => set("cta_product_id", e.target.value)} className="w-full h-10 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
                <option value="">— seleziona libro Amazon —</option>
                {amazonProducts.map((p) => <option key={p.id} value={p.id}>{p.title_it}</option>)}
              </select>
            )}
            {form.cta_type === "external" && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Titolo CTA IT"><Input data-testid="blog-cta-title-it" value={form.cta_title_it} onChange={(e) => set("cta_title_it", e.target.value)} /></Field>
                  <Field label="Titolo CTA EN"><Input value={form.cta_title_en} onChange={(e) => set("cta_title_en", e.target.value)} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Testo CTA IT"><Textarea rows={2} value={form.cta_text_it} onChange={(e) => set("cta_text_it", e.target.value)} /></Field>
                  <Field label="Testo CTA EN"><Textarea rows={2} value={form.cta_text_en} onChange={(e) => set("cta_text_en", e.target.value)} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Etichetta pulsante IT"><Input value={form.cta_button_it} onChange={(e) => set("cta_button_it", e.target.value)} /></Field>
                  <Field label="Etichetta pulsante EN"><Input value={form.cta_button_en} onChange={(e) => set("cta_button_en", e.target.value)} /></Field>
                </div>
                <Field label="URL (https://...)"><Input data-testid="blog-cta-url" value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} /></Field>
                <label className="flex items-center gap-2"><Switch checked={form.cta_new_tab} onCheckedChange={(v) => set("cta_new_tab", v)} /> <span className="text-sm">Apri in nuova scheda</span></label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Disclosure commerciale IT"><Textarea rows={2} value={form.cta_disclosure_it} onChange={(e) => set("cta_disclosure_it", e.target.value)} placeholder="es. Questo è un link a un sito esterno." /></Field>
                  <Field label="Disclosure commerciale EN"><Textarea rows={2} value={form.cta_disclosure_en} onChange={(e) => set("cta_disclosure_en", e.target.value)} /></Field>
                </div>
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="rounded-xl border border-brand-navy/10 p-4 space-y-3">
            <p className="text-sm font-semibold text-brand-navy">SEO</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="SEO Title IT"><Input value={form.seo_title_it} onChange={(e) => set("seo_title_it", e.target.value)} /></Field>
              <Field label="SEO Title EN"><Input value={form.seo_title_en} onChange={(e) => set("seo_title_en", e.target.value)} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="SEO Description IT"><Textarea rows={2} value={form.seo_desc_it} onChange={(e) => set("seo_desc_it", e.target.value)} /></Field>
              <Field label="SEO Description EN"><Textarea rows={2} value={form.seo_desc_en} onChange={(e) => set("seo_desc_en", e.target.value)} /></Field>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="blog-save" onClick={save} disabled={saving} className="rounded-full bg-brand-electric text-white">{saving ? "…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BlogCategories = ({ onChanged }) => {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name_it: "", name_en: "", slug: "" });
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => { api.get("/admin/blog/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };
  const add = async () => {
    if (!form.name_it || !form.name_en) { toast.error("Nome IT/EN obbligatori"); return; }
    try { await api.post("/admin/blog/categories", form); setForm({ name_it: "", name_en: "", slug: "" }); refresh(); toast.success("Categoria creata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async (c) => { try { await api.put(`/admin/blog/categories/${c.id}`, { name_it: c.name_it, name_en: c.name_en }); setEditId(null); refresh(); toast.success("Salvato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const move = async (id, direction) => { try { await api.post(`/admin/blog/categories/${id}/move`, { direction }); refresh(); } catch { toast.error("Errore"); } };
  const toggle = async (c) => { try { await api.put(`/admin/blog/categories/${c.id}`, { visible: !c.visible }); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Eliminare la categoria?")) return; try { await api.delete(`/admin/blog/categories/${id}`); refresh(); toast.success("Eliminata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div data-testid="blog-categories" className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-2">
        {cats.map((c, i) => (
          <div key={c.id} data-testid={`blog-cat-row-${c.slug}`} className={`flex items-center gap-2 rounded-xl border border-brand-navy/10 bg-white p-3 ${!c.visible ? "opacity-60" : ""}`}>
            <div className="flex flex-col gap-0.5">
              <button data-testid={`blog-cat-up-${c.slug}`} disabled={i === 0} onClick={() => move(c.id, "up")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
              <button data-testid={`blog-cat-down-${c.slug}`} disabled={i === cats.length - 1} onClick={() => move(c.id, "down")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
            </div>
            {editId === c.id ? (
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input value={c.name_it} onChange={(e) => setCats((cs) => cs.map((x) => x.id === c.id ? { ...x, name_it: e.target.value } : x))} />
                <Input value={c.name_en} onChange={(e) => setCats((cs) => cs.map((x) => x.id === c.id ? { ...x, name_en: e.target.value } : x))} />
              </div>
            ) : (
              <div className="flex-1 min-w-0"><p className="font-semibold text-brand-navy truncate">{c.name_it} <span className="text-brand-navy/40 font-normal">/ {c.name_en}</span></p><p className="text-xs text-brand-navy/50 font-mono">{c.slug}</p></div>
            )}
            <div className="flex items-center gap-1.5">
              <button onClick={() => toggle(c)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center">{c.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
              {editId === c.id
                ? <button data-testid={`blog-cat-savedit-${c.slug}`} onClick={() => saveEdit(c)} className="h-8 px-3 rounded-lg border border-brand-electric text-brand-electric text-xs">Salva</button>
                : <button data-testid={`blog-cat-edit-${c.slug}`} onClick={() => setEditId(c.id)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center"><Pencil className="h-4 w-4" /></button>}
              <button data-testid={`blog-cat-del-${c.slug}`} onClick={() => del(c.id)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-brand-navy/10 bg-white p-5 h-fit">
        <h4 className="font-heading font-semibold text-brand-navy">Nuova categoria Blog</h4>
        <div className="mt-3 space-y-2">
          <Input data-testid="blog-newcat-it" placeholder="Nome IT" value={form.name_it} onChange={(e) => setForm({ ...form, name_it: e.target.value })} />
          <Input data-testid="blog-newcat-en" placeholder="Nome EN" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
          <Input data-testid="blog-newcat-slug" placeholder="slug (opzionale)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          <Button data-testid="blog-newcat-add" onClick={add} className="w-full rounded-full bg-brand-electric text-white gap-2"><Plus className="h-4 w-4" /> Aggiungi</Button>
        </div>
      </div>
    </div>
  );
};

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState("posts");

  const load = useCallback(() => {
    api.get("/admin/blog/posts", { params: filter ? { status: filter } : {} }).then(({ data }) => setPosts(data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const loadCats = useCallback(() => { api.get("/admin/blog/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => {
    loadCats();
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, [loadCats]);

  const catName = (slug) => { const c = cats.find((x) => x.slug === slug); return c ? c.name_it : slug; };
  const openNew = () => { loadCats(); setEditId(null); setFormOpen(true); };
  const openEdit = (id) => { setEditId(id); setFormOpen(true); };
  const publish = async (id) => { try { await api.post(`/admin/blog/posts/${id}/publish`); load(); toast.success("Stato aggiornato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const duplicate = async (id) => { try { await api.post(`/admin/blog/posts/${id}/duplicate`); load(); toast.success("Duplicato (bozza)"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Eliminare l'articolo?")) return; try { await api.delete(`/admin/blog/posts/${id}`); load(); toast.success("Eliminato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <button data-testid="blog-view-posts" onClick={() => setView("posts")} className={`rounded-full px-4 py-1.5 text-sm font-medium border ${view === "posts" ? "bg-brand-electric text-white border-brand-electric" : "border-brand-navy/15 text-brand-navy/70"}`}>Articoli</button>
          <button data-testid="blog-view-cats" onClick={() => setView("cats")} className={`rounded-full px-4 py-1.5 text-sm font-medium border ${view === "cats" ? "bg-brand-electric text-white border-brand-electric" : "border-brand-navy/15 text-brand-navy/70"}`}>Categorie Blog</button>
        </div>
        {view === "posts" && <Button data-testid="blog-new" onClick={openNew} className="rounded-full bg-brand-electric text-white gap-2"><Plus className="h-4 w-4" /> Nuovo articolo</Button>}
      </div>

      {view === "cats" ? <BlogCategories onChanged={loadCats} /> : (
        <>
          <div className="flex gap-2 mb-4">
            {[["", "Tutti"], ["draft", "Bozze"], ["published", "Pubblicati"]].map(([v, lbl]) => (
              <button key={v} data-testid={`blog-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`rounded-full px-4 py-1.5 text-sm border ${filter === v ? "bg-brand-navy text-white border-brand-navy" : "border-brand-navy/15 text-brand-navy/70"}`}>{lbl}</button>
            ))}
          </div>
          <div className="overflow-x-auto rounded-2xl border border-brand-navy/10 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
                <th className="p-3">Titolo</th><th className="p-3">Categoria</th><th className="p-3">Stato</th><th className="p-3">Data</th><th className="p-3">Ev.</th><th className="p-3">Azioni</th>
              </tr></thead>
              <tbody className="divide-y divide-brand-navy/5">
                {posts.map((p) => (
                  <tr key={p.id} data-testid={`blog-row-${p.slug}`} className="hover:bg-brand-light">
                    <td className="p-3 font-medium text-brand-navy">{p.title_it}</td>
                    <td className="p-3 text-brand-navy/60">{catName(p.category_slug)}</td>
                    <td className="p-3">{p.status === "published" ? <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">PUBBLICATO</span> : <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">BOZZA</span>}</td>
                    <td className="p-3 text-brand-navy/50 text-xs">{p.published_at ? new Date(p.published_at).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="p-3">{p.featured ? "⭐" : ""}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button data-testid={`blog-edit-${p.slug}`} onClick={() => openEdit(p.id)} className="text-brand-navy/60 text-xs underline">Modifica</button>
                        <button data-testid={`blog-duplicate-${p.slug}`} onClick={() => duplicate(p.id)} className="text-brand-navy/60 text-xs underline">Duplica</button>
                        <button data-testid={`blog-publish-${p.slug}`} onClick={() => publish(p.id)} className="text-brand-electric text-xs underline">{p.status === "published" ? "Nascondi" : "Pubblica"}</button>
                        <button data-testid={`blog-del-${p.slug}`} onClick={() => del(p.id)} className="text-red-500 text-xs underline">Elimina</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!posts.length && <tr><td colSpan={6} className="p-6 text-center text-brand-navy/40">Nessun articolo.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      <PostForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} editingId={editId} categories={cats} products={products} />
    </div>
  );
};

export default AdminBlog;
